import React from "react";
import { FieldGroup } from "../../../../components";
import { Icon, IconButton } from "../../../../components/ui";
import { requestTypes, sortByOptions } from "./constants";
import { isMobile } from "react-device-detect";
import BrandSelect from "./BrandSelect";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
  Label,
} from "recharts";

const AiPricingFilter = ({
  onNext,
  onPrevious,
  index,
  matchList,
  tab,
  requestType,
  sortBy,
  region,
  regionOptions,
  messageType,
  messageTypeOptions,
  startDate,
  endDate,
  setRequestType,
  setSortBy,
  setRegion,
  setMessageType,
  setStartDate,
  setEndDate,
  discoverType,
  setDiscoverType,
  brand,
  setBrand,
  isProductPreview,
  messageCounts,
  similarProducts,
  selectedReference,
  setSelectedReference,
  // "bar": the quote workspace — the same filters as one labelled row, without
  // the match arrows or the chart (the workspace draws those itself).
  layout = "column",
}) => {
  const requestTypeOption = isProductPreview
    ? [{ id: "Sell", name: "Sell" }]
    : requestTypes;

  if (layout === "bar") {
    const select = (name, label, options, value, onChange) => (
      <div className="qws-field" key={name}>
        <FieldGroup label={label} placeholder="Select…" type="select" options={options} name={name} noFormik value={value} onChange={onChange} />
      </div>
    );
    const date = (name, label, value, set) => (
      <div className="qws-field" key={name}>
        <FieldGroup
          label={label}
          name={name}
          dateFormat="yyyy-MM-dd"
          type="date"
          noFormik
          value={value}
          onChange={(d) => set(new Date(d).toISOString().slice(0, 10))}
        />
      </div>
    );
    const brandField = (
      <div className="qws-field" key="brand">
        <label className="qws-field__label">Brand</label>
        <BrandSelect selectedBrand={brand} setBrand={setBrand} isNonMatch={true} />
      </div>
    );
    const messages = [
      select("request_type", "Buying or selling", requestTypeOption, requestType, setRequestType),
      select("sort_by", "Sort", sortByOptions, sortBy, setSortBy),
      select("region", "Region", regionOptions, region, setRegion),
      date("start_date", "From", startDate, setStartDate),
      date("end_date", "To", endDate, setEndDate),
      select("message_type", "Messages", messageTypeOptions, messageType, setMessageType),
    ];
    const controls = {
      whatsapp: messages,
      similar_products: [
        select(
          "selected_reference",
          "Similar reference",
          similarProducts,
          selectedReference?.id || "",
          (value) => setSelectedReference(similarProducts.find((p) => p.id === value) || null)
        ),
        ...messages,
      ],
      discover: [messages[1], messages[2], messages[3], messages[4], brandField],
      marketplaces: [brandField],
    }[tab];

    return controls ? (
      <div className="qws-filters qws-filters--grid ai-pricing-filter-bar" role="group" aria-label="Filter matches">
        {controls}
      </div>
    ) : null;
  }

  return (
    <div className="d-flex flex-column ai-pricing-filter">
      {tab === "whatsapp" ? (
        <div className="row p-3">
          <div className={isMobile ? "col-6" : "col-12"}>
            <FieldGroup
              placeholder="Request Type"
              type="select"
              options={requestTypeOption}
              name="request_type"
              noFormik
              value={requestType}
              onChange={setRequestType}
            />
          </div>

          <div className={isMobile ? "col-6" : "col-12"}>
            <FieldGroup
              placeholder="Sort by"
              type="select"
              options={sortByOptions}
              name="sort_by"
              noFormik
              value={sortBy}
              onChange={setSortBy}
            />
          </div>

          <div className={isMobile ? "col-6" : "col-12"}>
            <FieldGroup
              placeholder="Region"
              type="select"
              options={regionOptions}
              name="region"
              noFormik
              value={region}
              onChange={setRegion}
            />
          </div>

          <div className={`d-flex ${isMobile ? "col-6" : "col-12"}`}>
            <div className="mr-2 w-50">
              <FieldGroup
                placeholder="Start date"
                name="start_date"
                dateFormat="yyyy-MM-dd"
                type="date"
                noFormik
                value={startDate}
                onChange={(date) =>
                  setStartDate(new Date(date).toISOString().slice(0, 10))
                }
              />
            </div>
            <div className="ml-2 w-50">
              <FieldGroup
                placeholder="End date"
                name="end_date"
                dateFormat="yyyy-MM-dd"
                type="date"
                noFormik
                value={endDate}
                onChange={(date) =>
                  setEndDate(new Date(date).toISOString().slice(0, 10))
                }
              />
            </div>
          </div>

          <div className={isMobile ? "col-6" : "col-12"}>
            <FieldGroup
              placeholder="Message type"
              type="select"
              options={messageTypeOptions}
              name="message_type"
              noFormik
              value={messageType}
              onChange={setMessageType}
            />
          </div>
        </div>
      ) : tab === "similar_products" ? (
        <div className="row p-3">
          <div className={isMobile ? "col-6" : "col-12"}>
            <FieldGroup
              placeholder="Reference Number | Dial Colour"
              type="select"
              options={similarProducts}
              name="selected_reference"
              noFormik
              value={selectedReference?.id || ""}
              onChange={(value) => {
                const selected = similarProducts.find((p) => p.id === value);
                setSelectedReference(selected || null);
              }}
            />
          </div>

          <div className={isMobile ? "col-6" : "col-12"}>
            <FieldGroup
              placeholder="Request Type"
              type="select"
              options={requestTypeOption}
              name="request_type"
              noFormik
              value={requestType}
              onChange={setRequestType}
            />
          </div>

          <div className={isMobile ? "col-6" : "col-12"}>
            <FieldGroup
              placeholder="Sort by"
              type="select"
              options={sortByOptions}
              name="sort_by"
              noFormik
              value={sortBy}
              onChange={setSortBy}
            />
          </div>

          <div className={isMobile ? "col-6" : "col-12"}>
            <FieldGroup
              placeholder="Region"
              type="select"
              options={regionOptions}
              name="region"
              noFormik
              value={region}
              onChange={setRegion}
            />
          </div>

          <div className={`d-flex ${isMobile ? "col-6" : "col-12"}`}>
            <div className="mr-2 w-50">
              <FieldGroup
                placeholder="Start date"
                name="start_date"
                dateFormat="yyyy-MM-dd"
                type="date"
                noFormik
                value={startDate}
                onChange={(date) =>
                  setStartDate(new Date(date).toISOString().slice(0, 10))
                }
              />
            </div>
            <div className="ml-2 w-50">
              <FieldGroup
                placeholder="End date"
                name="end_date"
                dateFormat="yyyy-MM-dd"
                type="date"
                noFormik
                value={endDate}
                onChange={(date) =>
                  setEndDate(new Date(date).toISOString().slice(0, 10))
                }
              />
            </div>
          </div>

          <div className={isMobile ? "col-6" : "col-12"}>
            <FieldGroup
              placeholder="Message type"
              type="select"
              options={messageTypeOptions}
              name="message_type"
              noFormik
              value={messageType}
              onChange={setMessageType}
            />
          </div>
        </div>
      ) : tab === "discover" ? (
        <div className="row p-3">
          <div className={isMobile ? "col-6" : "col-12"}>
            <FieldGroup
              placeholder="Sort by"
              type="select"
              options={sortByOptions}
              name="sort_by"
              noFormik
              value={sortBy}
              onChange={setSortBy}
            />
          </div>

          <div className={isMobile ? "col-6" : "col-12"}>
            <FieldGroup
              placeholder="Region"
              type="select"
              options={regionOptions}
              name="region"
              noFormik
              value={region}
              onChange={setRegion}
            />
          </div>

          <div className={`d-flex ${isMobile ? "col-6" : "col-12"}`}>
            <div className="mr-2 w-50">
              <FieldGroup
                placeholder="Start date"
                name="start_date"
                dateFormat="yyyy-MM-dd"
                type="date"
                noFormik
                value={startDate}
                onChange={(date) =>
                  setStartDate(new Date(date).toISOString().slice(0, 10))
                }
              />
            </div>
            <div className="ml-2 w-50">
              <FieldGroup
                placeholder="End date"
                name="end_date"
                dateFormat="yyyy-MM-dd"
                type="date"
                noFormik
                value={endDate}
                onChange={(date) =>
                  setEndDate(new Date(date).toISOString().slice(0, 10))
                }
              />
            </div>
          </div>

          <div className={isMobile ? "col-4" : "col-12"}>
            <BrandSelect
              selectedBrand={brand}
              setBrand={setBrand}
              isNonMatch={true}
            />
          </div>
        </div>
      ) : tab === "marketplaces" ? (
        <div className="row p-3">
          <div className={isMobile ? "col-6" : "col-12"}>
            <BrandSelect
              selectedBrand={brand}
              setBrand={setBrand}
              isNonMatch={true}
            />
          </div>
        </div>
      ) : null}
      {matchList.length > 0 && (
        <div className="d-flex justify-content-between p-3">
          <IconButton
            variant="ghost"
            label="Previous match"
            data-btn="extraction-match-prev"
            onClick={onPrevious}
          >
            <Icon name="ChevronLeft" />
          </IconButton>
          <p className="my-auto">{`${
            matchList.length > 0 ? index + 1 : 0
          }/${matchList.length}`}</p>
          <IconButton
            variant="ghost"
            label="Next match"
            data-btn="extraction-match-next"
            onClick={onNext}
          >
            <Icon name="ChevronRight" />
          </IconButton>
        </div>
      )}
      {messageCounts && (
        <div className="col-12 text-center mt-5">
          <h5 className="">Data to Approve</h5>
          <div style={{ width: "100%", maxWidth: "400px", margin: "0 auto" }}>
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  dataKey="value"
                  data={[
                    {
                      name: "Buy (Single)",
                      value: messageCounts.buy_single,
                      fill: "#299861",
                    },
                    {
                      name: "Buy (Multi)",
                      value: messageCounts.buy_multi,
                      fill: "#a0d1b9",
                    },
                    {
                      name: "Sell (Single)",
                      value: messageCounts.sell_single,
                      fill: "#D62828",
                    },
                    {
                      name: "Sell (Multi)",
                      value: messageCounts.sell_multi,
                      fill: "#E06B6B",
                    },
                  ]}
                  innerRadius={60}
                  outerRadius={90}
                  labelLine={false}
                >
                  <Label
                    value={
                      messageCounts.buy_single +
                      messageCounts.buy_multi +
                      messageCounts.sell_single +
                      messageCounts.sell_multi
                    }
                    position="center"
                    style={{ fontSize: "20px", fontWeight: "bold" }}
                  />
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
            <div style={{ textAlign: "left", display: "inline-block" }}>
              <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                <li style={{ textAlign: "left" }}>
                  <span style={{ color: "#299861" }}>■</span> Buy (Single):{" "}
                  {messageCounts.buy_single}
                </li>
                <li style={{ textAlign: "left" }}>
                  <span style={{ color: "#a0d1b9" }}>■</span> Buy (Multi):{" "}
                  {messageCounts.buy_multi}
                </li>
                <li style={{ textAlign: "left" }}>
                  <span style={{ color: "#D62828" }}>■</span> Sell (Single):{" "}
                  {messageCounts.sell_single}
                </li>
                <li style={{ textAlign: "left" }}>
                  <span style={{ color: "#E06B6B" }}>■</span> Sell (Multi):{" "}
                  {messageCounts.sell_multi}
                </li>
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AiPricingFilter;
